// START IMPORTS
// Theme and UI imports
import { Box, Stack } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  DateUtil,
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  useReport,
  ReportLoading,
  UsernameOverview,
  ReportOptions,
  ReportFactory,
  ReportNullState,
  DebugMenu,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  AddressHistorySection,
  EducationSection,
  EmailSection,
  JobsSection,
  NotesSection,
  PhoneSection,
  PotentialOwners,
  RelatedReportsSection,
  ReportActionsWithDateUpdated,
  ReportNavigationMenu,
  ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  ReportRating,
  SocialSection,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getSocialNavLinkData } from 'navLinkData/socialNavLinkData';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface SocialReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'username';
// END CONSTANTS

export function SocialReportPage({ permalink }: SocialReportProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: true,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  // END STATE_MANAGEMENT

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={9} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'username');

  if (!report) return <ReportLoading menuItems={9} />;

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    relatives,
  } = owner;

  const socialNavLinkData = getSocialNavLinkData(
    owner,
    report?.data?.people?.length ?? 0
  );

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  document.title = `${report.username} - PeopleLooker`;
  const isUsernamePartialAddress = useFeatureIsOn('OAR-2083');
  const alternativeTitle = isUsernamePartialAddress
    ? 'Possible Locations'
    : undefined;
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Social Report"
            headerTitle={ownerName || report.username}
            menuItems={
              socialNavLinkData as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          <Box
            sx={{
              height: 30,
              backgroundColor: 'background.default',
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                sm: 'block',
                md: 'block',
                lg: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Username"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <UsernameOverview
            reportType={reportType}
            username={report.username}
            showHighConfidenceToggle
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) =>
              handleSetReportOptions({ showHighConfidenceDataOnly: value })
            }
          />
          <PotentialOwners
            potentialOwners={report.data.people || []}
            potentialOwnerIndex={reportOptions.potentialOwnerIndex}
            setPotentialOwnerIndex={(index: number) => {
              handleSetReportOptions({ potentialOwnerIndex: index });
            }}
            personName={ownerName || report.username}
            permalink={permalink}
            reportType={reportType}
          />
          <PhoneSection
            personName={ownerName || report.username}
            phoneList={phones}
            permalink={permalink}
          />
          <EmailSection
            personName={ownerName || report.username}
            emailList={emails}
            permalink={permalink}
          />
          <AddressHistorySection
            addresses={addresses}
            personName={ownerName || report.username}
            permalink={permalink}
            alternateTitle={alternativeTitle}
          />
          <SocialSection
            personName={ownerName || report.username}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          <JobsSection
            personName={ownerName || report.username}
            jobs={jobs}
            permalink={permalink}
          />

          <EducationSection
            personName={ownerName || report.username}
            educations={educations}
            permalink={permalink}
          />

          <NotesSection permalink={permalink} />
          <RelatedReportsSection
            reportType="username"
            personName={ownerName || report.username}
            potentialOwners={report.data.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />
          <ReportRating
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Social Report"
        headerTitle={ownerName || report.username}
        menuItems={socialNavLinkData as ReportNavigationMenuProps['menuItems']}
        isMobileBannerShowed={false}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
